import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import format from 'string-template';
import moment from 'moment';
import { IconButton, Tooltip } from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  InfoOutlined as InfoOutlinedIcon,
} from '@mui/icons-material';

import { CyclePhase, DATE_FORMAT, getCycleWeekSummary } from '../../../utils/cycle';
import {
  Container,
  Header,
  CurrentPhase,
  SmallLabel,
  PhaseHeading,
  PhaseSwatch,
  PhaseName,
  PhaseDay,
  PhaseGuide,
  PhaseGuideItem,
  PhaseGuideName,
  StatusPill,
  Legend,
  LegendItem,
  WeekNav,
  WeekRange,
  WeekRangeLabel,
  WeekRangeHint,
  BackToCheckIn,
  WeekGrid,
  DayCard,
  DayLabel,
  DateCircle,
  DaySymptoms,
  Footnote,
} from './styles';
import texts from './texts.json';

// How far the coach can page from the check-in week: back over the year of logs that is loaded, and forward far
// enough to see the next few predicted periods.
const MIN_WEEK_OFFSET = -52;
const MAX_WEEK_OFFSET = 12;

// In cycle order, for the phase guide behind the info icon.
const PHASE_ORDER = [CyclePhase.MENSTRUAL, CyclePhase.FOLLICULAR, CyclePhase.OVULATORY, CyclePhase.LUTEAL];

const phaseGuide = (
  <PhaseGuide>
    {PHASE_ORDER.map((phase) => (
      <PhaseGuideItem key={phase}>
        <PhaseGuideName>{texts.phaseLabels[phase]}</PhaseGuideName>
        {texts.phaseDescriptions[phase]}
      </PhaseGuideItem>
    ))}
  </PhaseGuide>
);

const phaseStatus = (isEstimated) => (isEstimated ? texts.estimated : texts.logged);

// Days are the client's local dates (YYYY-MM-DD); these read them without any timezone shift.
const asDay = (dateId) => moment(dateId, DATE_FORMAT);

/**
 * Where the week shown sits: the check-in week, an earlier week, a later week that has already happened (logged days
 * of an older check-in), or one still to come, whose phases can only be estimated.
 */
const weekHint = (weekOffset, lastDayId, todayId) => {
  if (weekOffset === 0) return texts.checkInWeek;
  if (weekOffset < 0) return texts.pastWeek;
  return lastDayId > todayId ? texts.futureWeek : texts.laterWeek;
};

/**
 * The client's cycle context on a check-in: the phase on the last day of the week shown, then one card per day with
 * its phase and the symptoms logged that day. Logged days are solid and estimated ones dashed. It opens on the
 * check-in week; the coach can page back through logged weeks and forward through estimated ones. An info icon by the
 * phase explains every phase. A week with no derivable phase (symptoms only) leaves out the phase header and legend.
 */
const CycleStat = ({
  settings, dayLogs, checkInDateId, todayId,
}) => {
  const [weekOffset, setWeekOffset] = useState(0);
  const windowEndId = asDay(checkInDateId).add(weekOffset * 7, 'days').format(DATE_FORMAT);
  const { days, currentPhase } = useMemo(
    () => getCycleWeekSummary(settings, dayLogs, windowEndId),
    [settings, dayLogs, windowEndId],
  );

  const isCheckInWeek = weekOffset === 0;
  const lastDay = days[days.length - 1];
  // One legend entry per phase and status that appears in the week, in the order they first appear.
  const legend = [...new Map(days
    .filter(({ phase }) => !!phase)
    .map(({ phase, isEstimated }) => [`${phase}-${isEstimated}`, { phase, isEstimated }])).values()];
  const hasEstimatedDays = days.some(({ phase, isEstimated }) => !!phase && isEstimated);
  const rangeLabel = `${asDay(days[0].dateId).format('MMM D')} – ${asDay(lastDay.dateId).format('MMM D')}`;

  return (
    <Container>
      <WeekNav>
        <IconButton
          size="small"
          aria-label={texts.previousWeek}
          disabled={weekOffset <= MIN_WEEK_OFFSET}
          onClick={() => setWeekOffset((offset) => offset - 1)}
        >
          <ChevronLeftIcon fontSize="small" />
        </IconButton>
        <WeekRange>
          <WeekRangeLabel>{rangeLabel}</WeekRangeLabel>
          <WeekRangeHint>{weekHint(weekOffset, lastDay.dateId, todayId)}</WeekRangeHint>
        </WeekRange>
        <IconButton
          size="small"
          aria-label={texts.nextWeek}
          disabled={weekOffset >= MAX_WEEK_OFFSET}
          onClick={() => setWeekOffset((offset) => offset + 1)}
        >
          <ChevronRightIcon fontSize="small" />
        </IconButton>
        {!isCheckInWeek && (
          <BackToCheckIn type="button" onClick={() => setWeekOffset(0)}>{texts.backToCheckIn}</BackToCheckIn>
        )}
      </WeekNav>
      {!!currentPhase && (
        <Header>
          <CurrentPhase>
            <SmallLabel>
              {isCheckInWeek
                ? texts.currentPhase
                : format(texts.phaseOn, { date: asDay(lastDay.dateId).format('ddd, MMM D') })}
            </SmallLabel>
            <PhaseHeading>
              <PhaseSwatch $phase={currentPhase.phase} $isEstimated={currentPhase.isEstimated} />
              <PhaseName>{texts.phaseLabels[currentPhase.phase]}</PhaseName>
              <PhaseDay>{format(texts.day, { day: currentPhase.cycleDay })}</PhaseDay>
              <StatusPill $phase={currentPhase.phase} $isEstimated={currentPhase.isEstimated}>
                {phaseStatus(currentPhase.isEstimated)}
              </StatusPill>
              <Tooltip title={phaseGuide} placement="bottom-start" enterTouchDelay={0} arrow>
                <IconButton size="small" aria-label={texts.phaseGuide}>
                  <InfoOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </PhaseHeading>
          </CurrentPhase>
          <Legend>
            {legend.map(({ phase, isEstimated }) => (
              <LegendItem key={`${phase}-${isEstimated}`}>
                <PhaseSwatch $phase={phase} $isEstimated={isEstimated} />
                {`${texts.phaseLabels[phase]} · ${phaseStatus(isEstimated).toLowerCase()}`}
              </LegendItem>
            ))}
          </Legend>
        </Header>
      )}
      <WeekGrid>
        {days.map(({
          dateId, phase, isEstimated, symptoms,
        }) => {
          // The check-in day stands out in its own week, and today (the client's) in any week it falls in.
          const isToday = dateId === todayId;
          const isCheckInDay = dateId === checkInDateId;
          const isHighlighted = isCheckInDay || isToday;
          const dayName = asDay(dateId).format('ddd');
          return (
            <DayCard key={dateId} $phase={isHighlighted ? phase : null}>
              <DayLabel $phase={isHighlighted ? phase : null}>
                {isHighlighted ? `${dayName} · ${isToday ? texts.today : texts.checkIn}` : dayName}
              </DayLabel>
              <DateCircle $phase={phase} $isEstimated={isEstimated}>{asDay(dateId).format('D')}</DateCircle>
              <DaySymptoms>{symptoms.length > 0 ? symptoms.join(' · ') : texts.noSymptoms}</DaySymptoms>
            </DayCard>
          );
        })}
      </WeekGrid>
      {hasEstimatedDays && <Footnote>{texts.footnote}</Footnote>}
    </Container>
  );
};

CycleStat.propTypes = {
  settings: PropTypes.shape({
    periodLength: PropTypes.number,
    cycleLength: PropTypes.number,
    lastPeriodStart: PropTypes.string,
  }).isRequired,
  dayLogs: PropTypes.objectOf(PropTypes.shape({
    date: PropTypes.string,
    isPeriod: PropTypes.bool,
    symptoms: PropTypes.arrayOf(PropTypes.string),
  })).isRequired,
  // The check-in day and today, as the client's local dates (YYYY-MM-DD). The view opens on the check-in week.
  checkInDateId: PropTypes.string.isRequired,
  todayId: PropTypes.string.isRequired,
};

export default CycleStat;
